import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Badge, Icon, Segmented, MobileHeader, ThemeToggle, type IconName } from '@/components';
import { useAuth } from '@/auth/AuthProvider';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { titledName } from '@/lib/format';
import { EditProfileModal } from './EditProfileModal';

export function LearnerProfile() {
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const isDesktop = useIsDesktop();
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState<'profile' | 'settings'>('profile');
  if (!user) return null;

  // Mobile has no header Console button like desktop — give staff with console
  // permission a way into the admin/instructor side (nav by permission).
  const consolePath = role === 'instructor' ? '/teach' : role === 'admin' || role === 'superadmin' ? '/admin' : null;

  const rows: { l: string; v: string; th?: string; mono?: boolean }[] = [
    { l: 'Title', v: user.title },
    { l: 'Legal name', v: `${user.firstEn} ${user.lastEn}`, th: `${user.firstTh} ${user.lastTh}` },
    { l: 'Department', v: user.deptId },
    { l: 'Unit', v: user.unitId },
    { l: 'Email', v: user.email },
  ];

  const settings: { l: string; d?: string; ic: IconName }[] = [
    { l: 'Verify a certificate', ic: 'shield' },
  ];

  return (
    <div style={{ background: 'var(--paper)', minHeight: '100%' }}>
      <MobileHeader title="Profile" th="โปรไฟล์" />
      <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20, padding: isDesktop ? 32 : '20px 20px 28px' }}>
        <div className="card" style={{ padding: '26px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <Avatar initials={user.initials} size={76} tone={user.tone} photoUrl={user.photoUrl} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 19, fontWeight: 600 }}>{titledName(user, 'en')}</div>
            <div className="th" style={{ fontSize: 13.5, color: 'var(--ink-3)', marginTop: 2 }}>{titledName(user, 'th')}</div>
            <div style={{ marginTop: 10 }}><Badge kind="sage" dot>{user.role} · {user.unitId}</Badge></div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}><Icon name="user" size={14} /> Edit profile</button>
        </div>

        {!isDesktop && consolePath && (
          <button className="btn btn-soft btn-block" onClick={() => navigate(consolePath)}>
            <Icon name="shield" size={17} /> {role === 'instructor' ? 'Instructor console' : 'Admin console'} <Icon name="arrowR" size={16} />
          </button>
        )}

        <Segmented
          value={tab}
          onChange={setTab}
          options={[
            { value: 'profile', label: 'User profile' },
            { value: 'settings', label: 'Settings' },
          ]}
        />

        {tab === 'profile' ? (
          <div className="card divide">
            {rows.map((r, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '13px 16px' }}>
                <span style={{ fontSize: 14, color: 'var(--ink-3)', flex: 'none' }}>{r.l}</span>
                <span style={{ fontSize: 14.5, fontWeight: 500, textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere' }} className={r.mono ? 'u-mono' : ''}>
                  {r.v}
                  {r.th && <div className="th" style={{ fontSize: 12.5, color: 'var(--ink-3)', fontWeight: 400 }}>{r.th}</div>}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="card divide">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px' }}>
              <span style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 14.5, fontWeight: 500 }}>
                <Icon name="layers2" size={19} style={{ color: 'var(--ink-3)' }} /> Appearance · ธีม
              </span>
              <ThemeToggle />
            </div>
            {settings.map((s, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', cursor: 'pointer' }}>
                <span style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 14.5, fontWeight: 500 }}>
                  <Icon name={s.ic} size={19} style={{ color: 'var(--ink-3)' }} /> {s.l}
                </span>
                <span style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--ink-3)', fontSize: 13.5 }}>
                  {s.d}
                  <Icon name="chevR" size={16} />
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="u-mono" style={{ textAlign: 'center', fontSize: 11, color: 'var(--ink-4)', letterSpacing: '.08em' }}>WARM PAPER ED · v1.0</div>
      </div>
      <EditProfileModal open={editing} onClose={() => setEditing(false)} user={user} />
    </div>
  );
}
